/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * v2 record-page field section (design-reference "Sales · Deal record"):
 * a bordered card with a header row (icon, title, optional count and
 * actions), a hairline, then a two-column field grid.
 *
 * Detail pages keep their own field rendering (InlineEditField already draws
 * the v2 label/value pair); they hand that body to UiDetailSection, which
 * shows it in a DetailSection under v2 and returns the page's legacy markup
 * untouched otherwise.
 *
 * Inside a page Card (the small Travel pages, EntityDetail) the section goes
 * flat, so the frame and the section never draw two borders.
 */

import { Children, isValidElement, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import {
  Building2,
  ChevronDown,
  CreditCard,
  FileText,
  Handshake,
  History,
  Home,
  Info,
  Link2,
  ListPlus,
  MapPin,
  Package,
  Pencil,
  SlidersHorizontal,
  User,
  Users,
  Wallet,
} from "lucide-react";
import { v2 as t, v2Density as d, V2_DISPLAY_FONT_FAMILY, V2_FONT_FAMILY } from "styles/tokens/v2";

// First match wins, so specific words come before generic ones.
const SECTION_ICONS = [
  ["audit", History],
  ["custom", SlidersHorizontal],
  ["property", Home],
  ["address", MapPin],
  ["additional", ListPlus],
  ["edit", Pencil],
  ["partner", Users],
  ["account", Building2],
  ["deal", Handshake],
  ["related", Link2],
  ["financ", Wallet],
  ["pricing", Wallet],
  ["price", Wallet],
  ["payment", CreditCard],
  ["inventory", Package],
  ["product", Package],
  ["contact", User],
  ["basic", Info],
];

/** Lucide icon for a section title; FileText when nothing matches. */
export function sectionIcon(title) {
  const key = typeof title === "string" ? title.toLowerCase() : "";
  // Match at a word start, so "Credit terms" is not an "edit" section.
  const hit = SECTION_ICONS.find(([word]) => new RegExp(`\\b${word}`).test(key));
  return hit ? hit[1] : FileText;
}

const isV2Theme = (theme) => theme?.pearlUi === "v2";

export function DetailSection({ title, icon, count, actions, collapsible, defaultCollapsed, children, sx, ...rest }) {
  const [open, setOpen] = useState(!defaultCollapsed);
  const Icon = icon === undefined ? sectionIcon(title) : icon;
  const hasHeader = Boolean(title || actions);
  const expanded = !collapsible || open;

  const heading = (
    <>
      {Icon ? (
        <Box component="span" aria-hidden sx={{ display: "inline-flex", color: t.text2, flexShrink: 0 }}>
          <Icon size={16} strokeWidth={2} />
        </Box>
      ) : null}
      <Typography
        component="h3"
        sx={{
          fontFamily: V2_DISPLAY_FONT_FAMILY,
          fontSize: 15,
          fontWeight: 600,
          lineHeight: 1.3,
          color: t.text,
          m: 0,
          minWidth: 0,
          overflowWrap: "anywhere",
        }}
      >
        {title}
      </Typography>
      {count !== undefined && count !== null ? (
        <Box
          component="span"
          data-testid="detail-section-count"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            height: 20,
            px: 0.875,
            borderRadius: "10px",
            bgcolor: t.hover,
            color: t.text2,
            fontSize: 12,
            fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {count}
        </Box>
      ) : null}
    </>
  );

  return (
    <Box
      component="section"
      className="pearl-detail-section"
      sx={[
        {
          bgcolor: t.surface,
          border: `1px solid ${t.border}`,
          borderRadius: `${d.cardRadiusPx}px`,
          boxShadow: "none",
          fontFamily: V2_FONT_FAMILY,
          mb: 2,
          minWidth: 0,
          "&:last-child": { mb: 0 },
          // Flat inside a page Card: the Card is already the frame.
          ".MuiCard-root &": { bgcolor: "transparent", border: 0, borderRadius: 0, mb: 3 },
          ".MuiCard-root &:last-child": { mb: 0 },
          ".MuiCard-root & > .pearl-detail-section__header": { px: 0, pt: 0 },
          ".MuiCard-root & .pearl-detail-section__body": { px: 0, pb: 0 },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    >
      {hasHeader ? (
        <Box
          className="pearl-detail-section__header"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minHeight: 48,
            px: 2.5,
            py: 1,
            borderBottom: expanded ? `1px solid ${t.border}` : "none",
          }}
        >
          {collapsible ? (
            <Box
              component="button"
              type="button"
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flex: 1,
                minWidth: 0,
                p: 0,
                border: 0,
                bgcolor: "transparent",
                cursor: "pointer",
                textAlign: "left",
                font: "inherit",
                color: "inherit",
                borderRadius: `${d.radiusPx}px`,
                "&:focus-visible": { outline: `2px solid ${t.primary}`, outlineOffset: 2 },
              }}
            >
              {heading}
              <Box
                component="span"
                aria-hidden
                sx={{
                  display: "inline-flex",
                  ml: "auto",
                  color: t.text2,
                  transition: "transform 150ms ease",
                  transform: open ? "rotate(0deg)" : "rotate(-90deg)",
                }}
              >
                <ChevronDown size={16} />
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>{heading}</Box>
          )}
          {actions ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>{actions}</Box>
          ) : null}
        </Box>
      ) : null}
      <Collapse in={expanded} timeout={collapsible ? "auto" : 0} unmountOnExit={false}>
        <Box
          className="pearl-detail-section__body"
          sx={{
            p: 2.5,
            fontSize: 14,
            color: t.text,
            // Pages lay their fields out with <Grid container spacing={3}>:
            // keep the 24px columns, tighten rows to the 16px field rhythm.
            "& > .MuiGrid-container": { marginTop: "-16px" },
            "& > .MuiGrid-container > .MuiGrid-item": { paddingTop: "16px" },
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}

DetailSection.propTypes = {
  title: PropTypes.node,
  // A lucide icon component; null hides it; undefined picks one from the title.
  icon: PropTypes.oneOfType([PropTypes.elementType, PropTypes.oneOf([null, false])]),
  count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  actions: PropTypes.node,
  collapsible: PropTypes.bool,
  defaultCollapsed: PropTypes.bool,
  children: PropTypes.node,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

DetailSection.defaultProps = {
  title: null,
  icon: undefined,
  count: undefined,
  actions: null,
  collapsible: false,
  defaultCollapsed: false,
  children: null,
  sx: {},
};

/** Two columns from md up, one on phones. Children are DetailFields. */
export function DetailFieldGrid({ children, sx }) {
  return (
    <Box
      sx={[
        {
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" },
          columnGap: 3,
          rowGap: 2,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </Box>
  );
}

DetailFieldGrid.propTypes = {
  children: PropTypes.node,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

DetailFieldGrid.defaultProps = { children: null, sx: {} };

const isEmptyValue = (v) =>
  v === null || v === undefined || v === false || (typeof v === "string" && !v.trim());

// True when a node renders some text: a blank link (capitalizeFirstLetter(''))
// or an empty array counts as empty, so the field shows the placeholder.
const hasText = (node) => {
  if (Array.isArray(node)) return node.some(hasText);
  if (isValidElement(node)) return hasText(node.props.children);
  if (typeof node === "number") return true;
  return !isEmptyValue(node) && typeof node === "string";
};

/**
 * One read-only label/value pair, drawn like InlineEditField's v2 read view:
 * 12px label, 14px value, hairline under it. For values that do not go
 * through InlineEditField (audit links, property requirements).
 */
export function DetailField({ label, value, children, fullWidth }) {
  const content = children !== undefined && children !== null ? children : value;
  const empty = isEmptyValue(content);
  return (
    <Box
      sx={{
        pb: 1.25,
        borderBottom: `1px solid ${t.border}`,
        minWidth: 0,
        ...(fullWidth ? { gridColumn: "1 / -1" } : {}),
      }}
    >
      <Typography component="div" sx={{ fontSize: 12, lineHeight: 1.35, color: t.text2, mb: 0.5 }}>
        {label}
      </Typography>
      <Typography
        component="div"
        sx={{
          fontSize: 14,
          lineHeight: 1.45,
          color: empty ? t.textDisabled : t.text,
          overflowWrap: "break-word",
          fontVariantNumeric: "tabular-nums",
          // Legacy link spans carry ml: 1 after an inline "Label:"; the label sits above here.
          "& > *": { ml: "0 !important" },
        }}
      >
        {empty ? "—" : content}
      </Typography>
    </Box>
  );
}

DetailField.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.node,
  children: PropTypes.node,
  fullWidth: PropTypes.bool,
};

DetailField.defaultProps = { value: null, children: undefined, fullWidth: false };

/**
 * The switch detail pages use. v2: a DetailSection around `children`.
 * Legacy: `legacy`, the page's existing section markup, returned as is.
 */
export function UiDetailSection({ legacy, children, ...sectionProps }) {
  const theme = useTheme();
  if (!isV2Theme(theme)) return legacy;
  return <DetailSection {...sectionProps}>{children}</DetailSection>;
}

UiDetailSection.propTypes = {
  legacy: PropTypes.node,
  children: PropTypes.node,
};

UiDetailSection.defaultProps = { legacy: null, children: null };

/**
 * The "Created By: <link>" pair in renderSection bodies. Pages wrap their
 * existing markup (an MDBox holding a bold "Label:" then the link) in this.
 * Legacy returns that markup as is; v2 drops the inline label and stacks the
 * link under `label` like every other field.
 */
export function UiLabeledValue({ label, children }) {
  const theme = useTheme();
  if (!isV2Theme(theme)) return children;
  const box = Children.toArray(children)[0];
  const parts = isValidElement(box) ? Children.toArray(box.props.children).slice(1) : [];
  return <DetailField label={label}>{hasText(parts) ? parts : null}</DetailField>;
}

UiLabeledValue.propTypes = {
  label: PropTypes.node.isRequired,
  children: PropTypes.node,
};

UiLabeledValue.defaultProps = { children: null };

export default DetailSection;
